import React, { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Search, Volume2, Video, Music, Calendar } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { uploadAudio, uploadVideo, validateFile } from '../utils/cloudinaryUpload';
import '../styles/StoryCreationModal.css';

const StoryCreationModal = ({
  eventId = null,
  artworkId = null,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    title: '',
    story_text: '',
    story_type: 'artwork',
    vc_id: artworkId || '',
    event_id: eventId || null,
    artist_uuid: '', // This is the story writer
    featured: false,
    audio_url: '',
    video_url: ''
  });

  const [artworkSearch, setArtworkSearch] = useState('');
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [events, setEvents] = useState([]);
  const [writers, setWriters] = useState([]);
  const [writerSearch, setWriterSearch] = useState('');
  const [filteredWriters, setFilteredWriters] = useState([]);
  const [showWriterForm, setShowWriterForm] = useState(false);
  const [newWriter, setNewWriter] = useState({
    first_name: '',
    last_name: '',
    email: '',
    nickname: ''
  });
  
  const [creating, setCreating] = useState(false);
  const [creatingWriter, setCreatingWriter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');
  
  const audioFileRef = useRef(null);
  const videoFileRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [eventId]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      event_id: eventId || null,
      vc_id: artworkId ? artworkId : prev.vc_id
    }));
  }, [eventId, artworkId]);

  useEffect(() => {
    if (writerSearch) {
      const filtered = writers.filter(writer => {
        const fullName = `${writer.first_name} ${writer.last_name}`.toLowerCase();
        const nickname = (writer.nickname || '').toLowerCase();
        const email = writer.email.toLowerCase();
        const search = writerSearch.toLowerCase();

        return fullName.includes(search) ||
               nickname.includes(search) ||
               email.includes(search);
      });
      setFilteredWriters(filtered);
    } else {
      setFilteredWriters(writers);
    }
  }, [writerSearch, writers]);

  useEffect(() => {
    if (!artworkSearch) {
      setFilteredArtworks(artworks);
      return;
    }

    const searchValue = artworkSearch.toLowerCase();
    const filtered = artworks.filter((artwork) => {
      const titleMatch = (artwork.title || '').toLowerCase().includes(searchValue);
      const artistName = artwork.visual_artist ?
        `${artwork.visual_artist.first_name || ''} ${artwork.visual_artist.last_name || ''} ${artwork.visual_artist.nickname || ''}`.toLowerCase() : '';
      return titleMatch || artistName.includes(searchValue);
    });

    setFilteredArtworks(filtered);
  }, [artworkSearch, artworks]);

  const formatEventLabel = (event) => {
    if (!event) return 'Untitled Event';
    const displayDate = event.event_start ? new Date(event.event_start).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : null;
    return `${event.name || 'Untitled Event'}${displayDate ? ` — ${displayDate}` : ''}`;
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [artworkResponse, writerResponse, eventsResponse] = await Promise.all([
        supabase
          .from('vcs')
          .select(`
            vc_id,
            title,
            cover_image,
            visual_artist:users!uuid (uuid, first_name, last_name, nickname)
          `)
          .order('title', { ascending: true })
          .limit(500),
        supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email')
          .order('first_name'),
        supabase
          .from('events')
          .select('id, name, event_start')
          .order('event_start', { ascending: false })
          .limit(200)
      ]);

      let processedArtworks = [];
      if (artworkResponse.error) {
        console.error('Error loading artworks:', artworkResponse.error);
      } else {
        processedArtworks = (artworkResponse.data || []).map((item) => ({
          vc_id: item.vc_id,
          title: item.title,
          cover_image: item.cover_image,
          visual_artist: item.visual_artist || null
        }));
      }

      if (!processedArtworks.length) {
        const { data: fallbackArtworks, error: fallbackError } = await supabase
          .from('vcs')
          .select('vc_id, title, cover_image, artist_uuid')
          .order('title', { ascending: true })
          .limit(500);

        if (fallbackError) {
          console.error('Fallback artwork load failed:', fallbackError);
        } else {
          processedArtworks = (fallbackArtworks || []).map((item) => ({
            vc_id: item.vc_id,
            title: item.title,
            cover_image: item.cover_image,
            visual_artist: null,
            artist_uuid: item.artist_uuid || null
          }));

          const artistIds = [...new Set(processedArtworks
            .map((item) => item.artist_uuid)
            .filter((id) => Boolean(id)))];

          if (artistIds.length) {
            const { data: artistRecords, error: artistError } = await supabase
              .from('users')
              .select('uuid, first_name, last_name, nickname')
              .in('uuid', artistIds);

            if (artistError) {
              console.error('Fallback artist lookup failed:', artistError);
            } else {
              const artistMap = new Map((artistRecords || []).map((artist) => [artist.uuid, artist]));
              processedArtworks = processedArtworks.map((artwork) => ({
                vc_id: artwork.vc_id,
                title: artwork.title,
                cover_image: artwork.cover_image,
                visual_artist: artistMap.get(artwork.artist_uuid) || null
              }));
            }
          } else {
            processedArtworks = processedArtworks.map((artwork) => ({
              vc_id: artwork.vc_id,
              title: artwork.title,
              cover_image: artwork.cover_image,
              visual_artist: null
            }));
          }
        }
      }

    setArtworks(processedArtworks);
    setFilteredArtworks(processedArtworks);
    setArtworkSearch('');

      if (writerResponse.error) {
        console.error('Error loading users:', writerResponse.error);
      }
      setWriters(writerResponse.data || []);
      setFilteredWriters(writerResponse.data || []);

      if (eventsResponse.error) {
        console.error('Error loading events:', eventsResponse.error);
        setEvents([]);
      } else {
        let loadedEvents = eventsResponse.data || [];

        if (eventId && !loadedEvents.some((event) => event.id === eventId)) {
          const { data: focusedEvent, error: focusedEventError } = await supabase
            .from('events')
            .select('id, name, event_start')
            .eq('id', eventId)
            .single();

          if (!focusedEventError && focusedEvent) {
            loadedEvents = [focusedEvent, ...loadedEvents];
          }
        }

        const dedupedEvents = loadedEvents.reduce((accumulator, event) => {
          if (!accumulator.some((existing) => existing.id === event.id)) {
            accumulator.push(event);
          }
          return accumulator;
        }, []);

        setEvents(dedupedEvents);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (file, type) => {
    const validation = validateFile(file, type);
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    if (type === 'audio') {
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
      setFormData(prev => ({ ...prev, audio_url: '' })); // Clear URL if file is selected
    } else if (type === 'video') {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setFormData(prev => ({ ...prev, video_url: '' })); // Clear URL if file is selected
    }
  };

  const handleFileUpload = async (type) => {
    try {
      if (type === 'audio' && audioFile) {
        setUploadingAudio(true);
        const result = await uploadAudio(audioFile);
        setFormData(prev => ({ ...prev, audio_url: result.url }));
        setAudioFile(null);
        setAudioPreview('');
        alert('Audio uploaded successfully!');
      } else if (type === 'video' && videoFile) {
        setUploadingVideo(true);
        const result = await uploadVideo(videoFile);
        setFormData(prev => ({ ...prev, video_url: result.url }));
        setVideoFile(null);
        setVideoPreview('');
        alert('Video uploaded successfully!');
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      alert(`Failed to upload ${type}: ${error.message}`);
    } finally {
      setUploadingAudio(false);
      setUploadingVideo(false);
    }
  };

  const removeFile = (type) => {
    if (type === 'audio') {
      setAudioFile(null);
      setAudioPreview('');
      setFormData(prev => ({ ...prev, audio_url: '' }));
    } else if (type === 'video') {
      setVideoFile(null);
      setVideoPreview('');
      setFormData(prev => ({ ...prev, video_url: '' }));
    }
  };

  const handleWriterSelect = (writer) => {
    setFormData(prev => ({ ...prev, artist_uuid: writer.uuid }));
    setWriterSearch(`${writer.first_name} ${writer.last_name}${writer.nickname ? ` (${writer.nickname})` : ''}`);
  };

  const handleCreateWriter = async () => {
    if (!newWriter.first_name || !newWriter.last_name || !newWriter.email) {
      alert('Please fill in all required writer fields');
      return;
    }

    setCreatingWriter(true);
    try {
      // Check if user already exists by email
      const { data: existingUser } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', newWriter.email)
        .single();

      if (existingUser) {
        alert('A user with this email already exists');
        return;
      }

      // Create new user
      const { data: userData, error } = await supabase
        .from('users')
        .insert({
          first_name: newWriter.first_name,
          last_name: newWriter.last_name,
          email: newWriter.email,
          nickname: newWriter.nickname || null,
          created_at: new Date().toISOString()
        })
        .select('uuid, first_name, last_name, nickname, email')
        .single();

      if (error) throw error;

      // Add to writers list and select
      setWriters(prev => [userData, ...prev]);
      setFilteredWriters(prev => [userData, ...prev]);
      handleWriterSelect(userData);
      
      // Reset form and close
      setNewWriter({ first_name: '', last_name: '', email: '', nickname: '' });
      setShowWriterForm(false);
      
      alert('Writer created successfully!');
      
    } catch (error) {
      console.error('Error creating writer:', error);
      alert('Failed to create writer: ' + error.message);
    } finally {
      setCreatingWriter(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.story_text || !formData.vc_id || !formData.artist_uuid) {
      alert('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
              const { data, error } = await supabase
          .from('artspeaks_stories')
          .insert({
            title: formData.title,
            story_text: formData.story_text,
            story_type: formData.story_type,
            vc_id: formData.vc_id,
            event_id: formData.event_id,
            artist_uuid: formData.artist_uuid,
            featured: formData.featured,
            performed_at_event: Boolean(formData.event_id),
            visibility: 'public',
            audio_url: formData.audio_url || null,
            video_url: formData.video_url || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select('story_id')
          .single();

      if (error) throw error;

      // Add to user activity
      await supabase.from('user_activity').insert({
        uuid: formData.artist_uuid,
        story_id: data.story_id,
        vc_id: formData.vc_id,
        activity_type: 'created_story',
        timestamp: new Date().toISOString(),
        event_id: formData.event_id || null
      });

      alert('Story created successfully!');
      onSuccess && onSuccess(data);
      onClose();
      
    } catch (error) {
      console.error('Error creating story:', error);
      alert('Failed to create story: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content story-creation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Story</h2>
          <button onClick={onClose} className="close-btn">
            <X className="close-icon" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Story Details */}
          <div className="form-section">
            <h3>Story Details</h3>
            
            <div className="form-group">
              <label>Story Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter story title"
                required
              />
            </div>

            <div className="form-group">
              <label>Story Type</label>
              <select
                value={formData.story_type}
                onChange={(e) => handleInputChange('story_type', e.target.value)}
              >
                <option value="artwork">Artwork Story</option>
                <option value="event">Event Story</option>
                <option value="artist">Artist Story</option>
                <option value="collection">Collection Story</option>
                <option value="general">General Story</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleInputChange('featured', e.target.checked)}
                />
                Featured Story
              </label>
            </div>
          </div>

          {/* Writer Selection */}
          <div className="form-section">
            <h3>Story Writer</h3>
            
            <div className="form-group">
              <label>Search Writer *</label>
              <div className="writer-search-container">
                <div className="search-input-container">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    value={writerSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWriterSearch(value);
                    }}
                    placeholder="Start typing writer name or email..."
                    className="writer-search-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowWriterForm(true)}
                  className="add-writer-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  New Writer
                </button>
              </div>
              
              {writerSearch && (
                <div className="writer-dropdown">
                  {filteredWriters.length > 0 ? (
                    filteredWriters.slice(0, 6).map(writer => (
                      <button
                        key={writer.uuid}
                        type="button"
                        className="writer-option"
                        onClick={() => handleWriterSelect(writer)}
                      >
                        <div className="writer-info">
                          <span className="writer-name">
                            {writer.first_name} {writer.last_name}
                            {writer.nickname && ` (${writer.nickname})`}
                          </span>
                          <span className="writer-email">{writer.email}</span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="no-writers">
                      No writers found. <button type="button" onClick={() => setShowWriterForm(true)}>Create new writer</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* New Writer Form */}
            {showWriterForm && (
              <div className="new-writer-form">
                <h4>Create New Writer</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={newWriter.first_name}
                      onChange={(e) => setNewWriter(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="First name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={newWriter.last_name}
                      onChange={(e) => setNewWriter(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newWriter.email}
                      onChange={(e) => setNewWriter(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Email address"
                    />
                  </div>
                  <div className="form-group">
                    <label>Nickname</label>
                    <input
                      type="text"
                      value={newWriter.nickname}
                      onChange={(e) => setNewWriter(prev => ({ ...prev, nickname: e.target.value }))}
                      placeholder="Nickname (optional)"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setShowWriterForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateWriter}
                    disabled={creatingWriter}
                    className="btn-primary"
                  >
                    {creatingWriter ? 'Creating...' : 'Create Writer'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Artwork Selection */}
          <div className="form-section">
            <h3>Artwork</h3>
            
            <div className="form-group">
              <label>Find Artwork</label>
              <div className="search-input-container">
                <Search className="search-icon" />
                <input
                  type="text"
                  value={artworkSearch}
                  onChange={(e) => setArtworkSearch(e.target.value)}
                  placeholder="Search artworks by title or artist..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Select Artwork *</label>
              <select
                value={formData.vc_id}
                onChange={(e) => handleInputChange('vc_id', e.target.value)}
                required
              >
                <option value="">Choose artwork...</option>
                {filteredArtworks.map(artwork => (
                  <option key={artwork.vc_id} value={artwork.vc_id}>
                    {artwork.title} - by {artwork.visual_artist ? 
                      (artwork.visual_artist.nickname || `${artwork.visual_artist.first_name} ${artwork.visual_artist.last_name}`) :
                      'Unknown Artist'
                    }
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Event Selection */}
          <div className="form-section">
            <h3>Event (Optional)</h3>
            <div className="form-group">
              <label>Associate Event</label>
              <div className="event-select-row">
                <Calendar className="event-icon" />
                <select
                  value={formData.event_id || ''}
                  onChange={(e) => handleInputChange('event_id', e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">No event</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {formatEventLabel(event)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Story Content */}
          <div className="form-section">
            <h3>Story Content</h3>
            
            <div className="form-group">
              <label>Story Text *</label>
              <textarea
                value={formData.story_text}
                onChange={(e) => handleInputChange('story_text', e.target.value)}
                placeholder="Enter the story content..."
                rows={8}
                required
              />
            </div>
          </div>

          {/* Media Upload */}
          <div className="form-section">
            <h3>Media (Optional)</h3>
            
            {/* Audio Upload */}
            <div className="form-group">
              <label>Audio Story</label>
              <div className="media-upload-container">
                <input
                  ref={audioFileRef}
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileSelect(e.target.files[0], 'audio')}
                  style={{ display: 'none' }}
                />
                
                {!audioFile && !formData.audio_url && (
                  <div className="upload-area">
                    <button
                      type="button"
                      onClick={() => audioFileRef.current?.click()}
                      className="upload-btn"
                    >
                      <Music className="w-4 h-4" />
                      Choose Audio File
                    </button>
                    <span className="upload-hint">MP3, WAV, M4A, AAC up to 50MB</span>
                  </div>
                )}

                {audioFile && (
                  <div className="file-preview">
                    <div className="file-info">
                      <Volume2 className="w-4 h-4" />
                      <span>{audioFile.name}</span>
                    </div>
                    <div className="file-actions">
                      <button
                        type="button"
                        onClick={() => handleFileUpload('audio')}
                        disabled={uploadingAudio}
                        className="btn-primary btn-sm"
                      >
                        {uploadingAudio ? 'Uploading...' : 'Upload'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFile('audio')}
                        className="btn-secondary btn-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {formData.audio_url && (
                  <div className="url-input">
                    <input
                      type="url"
                      value={formData.audio_url}
                      onChange={(e) => handleInputChange('audio_url', e.target.value)}
                      placeholder="Audio URL"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange('audio_url', '')}
                      className="btn-secondary btn-sm"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Video Upload */}
            <div className="form-group">
              <label>Video Story</label>
              <div className="media-upload-container">
                <input
                  ref={videoFileRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleFileSelect(e.target.files[0], 'video')}
                  style={{ display: 'none' }}
                />
                
                {!videoFile && !formData.video_url && (
                  <div className="upload-area">
                    <button
                      type="button"
                      onClick={() => videoFileRef.current?.click()}
                      className="upload-btn"
                    >
                      <Video className="w-4 h-4" />
                      Choose Video File
                    </button>
                    <span className="upload-hint">MP4, MOV, AVI, WebM up to 100MB</span>
                  </div>
                )}

                {videoFile && (
                  <div className="file-preview">
                    <div className="file-info">
                      <Video className="w-4 h-4" />
                      <span>{videoFile.name}</span>
                    </div>
                    <div className="file-actions">
                      <button
                        type="button"
                        onClick={() => handleFileUpload('video')}
                        disabled={uploadingVideo}
                        className="btn-primary btn-sm"
                      >
                        {uploadingVideo ? 'Uploading...' : 'Upload'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFile('video')}
                        className="btn-secondary btn-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {formData.video_url && (
                  <div className="url-input">
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => handleInputChange('video_url', e.target.value)}
                      placeholder="Video URL"
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange('video_url', '')}
                      className="btn-secondary btn-sm"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="btn-primary"
            >
              {creating ? 'Creating...' : 'Create Story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoryCreationModal;
